import React from "react";
import classnames from "classnames";

// Types
import type { GrailedAPILightListing } from "storefront/Listing/GrailedAPILightListing";
import type { AlgoliaMyItemsListing } from "storefront/Listing/AlgoliaMyItemsListing";
import type { AlgoliaListing } from "storefront/Listing/AlgoliaListing";

// Hooks
import usePublicConfig from "storefront/hooks/usePublicConfig";
import useListingStyles from "storefront/hooks/useListingStyles";

// Components
import { User } from "storefront/User";

// Styles
import styles from "./ListingMetadata.module.scss";

type Props = {
  listing: AlgoliaMyItemsListing | GrailedAPILightListing | AlgoliaListing;
  currentUser?: User | null;
  classNames?: {
    metadataClassName?: string;
    designerClassName?: string;
    titleClassName?: string;
  };
};

const StatusLabels = ({ listing }: Pick<Props, "listing">) => {
  const labels: Array<React.ReactNode> = [];

  if ("deleted" in listing && listing.deleted) {
    labels.push(
      <span key="deleted" className="red bold">
        DELETED
      </span>,
    );
  }

  if ("hidden" in listing && listing.hidden) {
    labels.push(
      <span key="hidden" className="blue bold">
        HIDDEN
      </span>,
    );
  }

  return <>{labels}</>;
};

const ListingMetadata = ({ listing, currentUser, classNames }: Props) => {
  const mergedClassNames = {
    metadataClassName: "",
    designerClassName: "",
    titleClassName: "",
    ...classNames,
  };

  const { unbrandedDesignerId } = usePublicConfig();

  const listingStyles = useListingStyles();

  const isUnbrandedListing = listing.designers.some(
    (designer) => String(designer.id) === String(unbrandedDesignerId),
  );

  const resolvedListingTitle = React.useMemo(() => {
    if (isUnbrandedListing && listing.styles?.length) {
      return listing.styles
        .map((style) => listingStyles[style]?.displayName || style)
        .join(", ");
    }
    return listing.designerNames;
  }, [
    isUnbrandedListing,
    listing.styles,
    listingStyles,
    listing.designerNames,
  ]);

  return (
    <div
      className={classnames(
        styles.metadata,
        mergedClassNames.metadataClassName,
      )}
    >
      <div className={styles.designerAndSize}>
        <p
          className={classnames(
            styles.designer,
            mergedClassNames.designerClassName,
          )}
        >
          {resolvedListingTitle}
        </p>
        <p className={styles.size}>
          {listing.size === "one size" ? "os" : listing.size}
        </p>
      </div>

      <div>
        {currentUser && currentUser.isAdmin ? (
          <StatusLabels listing={listing} />
        ) : null}
        <p
          className={classnames(styles.title, mergedClassNames.titleClassName)}
        >
          {listing.title}
        </p>
      </div>
    </div>
  );
};

export default ListingMetadata;
